import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, UserCog, RefreshCw, Download } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { downloadPdf } from '../../utils/pdfUtils'
import DateRangePicker from '../../components/ui/DateRangePicker'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700, textTransform: 'uppercase' },
  hdrT2: { fontSize: 14, fontWeight: 600, color: '#1f2937', marginTop: 3 },
  hdrT3: { fontSize: 13, color: '#374151', marginTop: 2 },
  hdrT4: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  secTitle: { fontSize: 15, fontWeight: 700, color: '#1f2937', marginTop: 20, marginBottom: 8, borderBottom: '1px solid #d1d5db', paddingBottom: 4 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12 },
  thL: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 12 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', verticalAlign: 'middle' },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', textAlign: 'right', verticalAlign: 'middle' },
  totRow: { fontWeight: 700, borderTop: '2px solid #f59e0b' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 13, marginBottom: 16, padding: '12px 0' },
  infoLbl: { fontWeight: 700, color: '#374151' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

// ─── Versão Básica ───────────────────────────────────────────────────────────
function VersaoBasica({ dados }) {
  return (
    <table style={S.tbl}>
      <thead><tr>
        <th style={S.thL}>Tipo</th>
        <th style={S.th}>%</th>
        <th style={S.th}>Lotes</th>
        <th style={S.th}>Qtde</th>
        <th style={S.thR}>Valor</th>
        <th style={S.thR}>Comissão</th>
      </tr></thead>
      <tbody>
        {(dados.tipos || []).map((t, i) => (
          <tr key={i}>
            <td style={S.td}>{t.tipo}</td>
            <td style={S.tdC}>{t.pct}%</td>
            <td style={S.tdC}>{t.lotes}</td>
            <td style={S.tdC}>{t.qtd}</td>
            <td style={S.tdR}>{brl(t.valor)}</td>
            <td style={S.tdR}>{brl(t.comissao)}</td>
          </tr>
        ))}
        <tr style={S.totRow}>
          <td style={{ ...S.td, borderTop: '2px solid #f59e0b' }}><strong>TOTAL</strong></td>
          <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}></td>
          <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{dados.total_lotes}</strong></td>
          <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{dados.total_qtd}</strong></td>
          <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_valor)}</strong></td>
          <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_comissao)}</strong></td>
        </tr>
      </tbody>
    </table>
  )
}

// ─── Versão Detalhada ────────────────────────────────────────────────────────
function VersaoDetalhada({ dados }) {
  return (
    <>
      {/* Resultado Geral */}
      <div style={S.secTitle}>Resultado Geral</div>
      <div style={S.infoGrid}>
        <div><span style={S.infoLbl}>Total de Vendas:</span> {brl(dados.total_valor_vendas)}</div>
        <div><span style={S.infoLbl}>Total de Animais:</span> {dados.total_qtd_vendas}</div>
        <div><span style={S.infoLbl}>Total de Defesas:</span> {brl(dados.total_valor_defesas)}</div>
        <div><span style={S.infoLbl}>Animais Defesa:</span> {dados.total_qtd_defesas}</div>
        <div><span style={S.infoLbl}>Total Geral:</span> {brl(dados.total_valor)}</div>
        <div><span style={S.infoLbl}>Total Animais:</span> {dados.total_qtd}</div>
      </div>

      {/* Breakdown por sexo */}
      <div style={S.secTitle}>Animais por Sexo</div>
      <table style={S.tbl}>
        <thead><tr>
          <th style={S.thL}>Sexo</th>
          <th style={S.th}>Qtde</th>
          <th style={S.thR}>Total R$</th>
          <th style={S.thR}>Média R$</th>
        </tr></thead>
        <tbody>
          {(dados.sexos || []).map((s, i) => (
            <tr key={i}>
              <td style={S.td}>{s.sexo}</td>
              <td style={S.tdC}>{s.qtd}</td>
              <td style={S.tdR}>{brl(s.valor)}</td>
              <td style={S.tdR}>{brl(s.media)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Comissão do Leiloeiro */}
      <div style={S.secTitle}>Comissão do Leiloeiro</div>
      <table style={S.tbl}>
        <thead><tr>
          <th style={S.thL}>Tipo</th>
          <th style={S.th}>%</th>
          <th style={S.th}>Lotes</th>
          <th style={S.th}>Qtde</th>
          <th style={S.thR}>Valor</th>
          <th style={S.thR}>Comissão</th>
        </tr></thead>
        <tbody>
          {(dados.tipos || []).map((t, i) => (
            <tr key={i}>
              <td style={S.td}>{t.tipo}</td>
              <td style={S.tdC}>{t.pct}%</td>
              <td style={S.tdC}>{t.lotes}</td>
              <td style={S.tdC}>{t.qtd}</td>
              <td style={S.tdR}>{brl(t.valor)}</td>
              <td style={S.tdR}>{brl(t.comissao)}</td>
            </tr>
          ))}
          <tr style={S.totRow}>
            <td style={{ ...S.td, borderTop: '2px solid #f59e0b' }}><strong>TOTAL</strong></td>
            <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}></td>
            <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{dados.total_lotes}</strong></td>
            <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{dados.total_qtd}</strong></td>
            <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_valor)}</strong></td>
            <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_comissao)}</strong></td>
          </tr>
        </tbody>
      </table>

      {/* Maior Comprador e Vendedor */}
      {(dados.maior_comprador || dados.maior_vendedor) && (
        <>
          <div style={S.secTitle}>Maior Comprador e Vendedor</div>
          <div style={S.infoGrid}>
            {dados.maior_comprador && <>
              <div><span style={S.infoLbl}>Comprador:</span> {dados.maior_comprador.nome}</div>
              <div><span style={S.infoLbl}>Compras:</span> {brl(dados.maior_comprador.valor)}</div>
            </>}
            {dados.maior_vendedor && <>
              <div><span style={S.infoLbl}>Vendedor:</span> {dados.maior_vendedor.nome}</div>
              <div><span style={S.infoLbl}>Vendas:</span> {brl(dados.maior_vendedor.valor)}</div>
            </>}
          </div>
        </>
      )}

      {/* Lote de Maior Cotação */}
      {dados.lote_maior && (
        <>
          <div style={S.secTitle}>Lote de Maior Cotação</div>
          <div style={S.infoGrid}>
            <div><span style={S.infoLbl}>Lote:</span> {dados.lote_maior.numero_lote}</div>
            <div><span style={S.infoLbl}>Valor Total:</span> {brl(dados.lote_maior.valor)}</div>
            <div><span style={S.infoLbl}>Vendedor:</span> {dados.lote_maior.vendedor}</div>
            <div><span style={S.infoLbl}>Qtde Animais:</span> {dados.lote_maior.qtd}</div>
            <div><span style={S.infoLbl}>Comprador:</span> {dados.lote_maior.comprador}</div>
            <div><span style={S.infoLbl}>Média:</span> {brl(dados.lote_maior.media)}</div>
          </div>
        </>
      )}

      {/* Lotes detalhados */}
      {dados.lotes?.length > 0 && (
        <>
          <div style={S.secTitle}>Lotes</div>
          <table style={S.tbl}>
            <thead><tr>
              <th style={S.thL}>Lote</th>
              <th style={S.thL}>Tipo</th>
              <th style={S.thL}>Vendedor</th>
              <th style={S.thL}>Comprador</th>
              <th style={S.th}>Qtde</th>
              <th style={S.thR}>Valor</th>
              <th style={S.th}>%</th>
              <th style={S.thR}>Comissão</th>
            </tr></thead>
            <tbody>
              {(dados.lotes || []).map((lt, i) => (
                <tr key={i}>
                  <td style={S.td}>{lt.numero_lote}</td>
                  <td style={S.td}>{lt.tipo}</td>
                  <td style={S.td}>{lt.vendedor}</td>
                  <td style={S.td}>{lt.comprador}</td>
                  <td style={S.tdC}>{lt.qtd}</td>
                  <td style={S.tdR}>{brl(lt.valor)}</td>
                  <td style={S.tdC}>{lt.pct.toFixed(2)}%</td>
                  <td style={S.tdR}>{brl(lt.comissao)}</td>
                </tr>
              ))}
              <tr style={S.totRow}>
                <td style={{ ...S.td, borderTop: '2px solid #f59e0b' }} colSpan={4}><strong>TOTAL</strong></td>
                <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{dados.total_qtd}</strong></td>
                <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_valor)}</strong></td>
                <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}></td>
                <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(dados.total_comissao)}</strong></td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
// Datas pelo calendário local (toISOString usa UTC e adianta o dia depois das 21h no Brasil)
const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
function primeiroDiaMes() {
  const d = new Date()
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1))
}
function hoje() { return isoLocal(new Date()) }

export default function ComissaoLeiloeiro() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [loadingL, setLoadingL] = useState(true)
  const [dataInicio, setDataInicio] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [versao, setVersao] = useState('basico')
  const [leiloeiroFiltro, setLeiloeiroFiltro] = useState('')
  const [dados, setDados] = useState(null)
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingXlsx, setLoadingXlsx] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  const filtro = modoPeriodo ? { dataInicio, dataFim } : { leilaoId }

  useEffect(() => {
    if (!orgId) return
    if (modoPeriodo ? !(dataInicio && dataFim) : !leilaoId) { setDados(null); return }
    let ativo = true
    setLoading(true)
    setErro('')
    setLeiloeiroFiltro('')
    mapasService.getComissaoLeiloeiroDados(orgId, filtro, versao)
      .then(res => { if (ativo) setDados(res) })
      .catch(err => { if (ativo) { setDados(null); setErro(err.response?.data?.detail || 'Nenhum leiloeiro encontrado') } })
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim, versao]) // eslint-disable-line

  const dadosArr = Array.isArray(dados) ? dados : (dados ? [dados] : [])
  const temMultiplosLeiloeiros = dadosArr.length > 1
  const dadosFiltrados = leiloeiroFiltro
    ? dadosArr.filter(d => String(d.leiloeiro_id) === leiloeiroFiltro)
    : dadosArr

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const fmtDateBR = s => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}` }
  const periodoLabel = modoPeriodo
    ? `${fmtDateBR(dataInicio)} — ${fmtDateBR(dataFim)}`
    : leilaoAtual ? `${leilaoAtual.nome} — ${fmtDate(leilaoAtual.data_leilao)}` : ''
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getComissaoLeiloeiroPdf(orgId, filtro, versao, leiloeiroFiltro || null)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }
  const handleExportarXlsx = async () => {
    if (!orgId) return
    setLoadingXlsx(true)
    try {
      const blob = await mapasService.getComissaoLeiloeiroXlsx(orgId, filtro, versao, leiloeiroFiltro || null)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `comissao_leiloeiro.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Erro ao exportar planilha: ' + (err.response?.statusText || err.message))
    } finally { setLoadingXlsx(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <UserCog className="w-5 h-5 text-cyan-600" />
        <h1 className="text-lg font-semibold text-gray-800">Comissão Leiloeiro</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 h-[38px] self-end">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-4 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Leilão
            </button>
            <button onClick={() => setModoPeriodo(true)}
              className={`px-4 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Período
            </button>
          </div>

          {!modoPeriodo ? (
            <Select label="Leilão" value={leilaoId} onChange={v => setLeilaoId(v)} disabled={loadingL} className="flex-[2] min-w-[200px]">
              {loadingL ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome} — {fmtDate(l.data_leilao)}</option>)}
            </Select>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Período</label>
              <DateRangePicker
                startDate={dataInicio}
                endDate={dataFim}
                onChange={({ data_inicio, data_fim }) => { setDataInicio(data_inicio); setDataFim(data_fim) }}
                className="min-w-[220px]"
              />
            </div>
          )}

          <Select label="Versão" value={versao} onChange={v => setVersao(v)} className="min-w-[140px]">
            <option value="basico">Básico</option>
            <option value="detalhado">Detalhado</option>
          </Select>
          {temMultiplosLeiloeiros && (
            <Select label="Leiloeiro" value={leiloeiroFiltro} onChange={setLeiloeiroFiltro} className="min-w-[160px]">
              <option value="">Todos</option>
              {dadosArr.map(d => (
                <option key={d.leiloeiro_id} value={String(d.leiloeiro_id)}>{d.leiloeiro_nome}</option>
              ))}
            </Select>
          )}
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleExportarXlsx} disabled={!dados || loadingXlsx}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              {loadingXlsx ? <RefreshCw size={15} className="animate-spin" /> : <Download size={15} />}
              Exportar Excel
            </button>
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : dadosFiltrados.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          {erro || (modoPeriodo ? 'Selecione um período' : 'Selecione um leilão')}
        </div>
      ) : (
        <div className="space-y-4">
          {dadosFiltrados.map((d, idx) => (
            <div key={d.leiloeiro_id || idx} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div style={S.page}>
                <div style={S.hdr}>
                  <div style={{ minWidth: 140 }}>
                    {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}
                  </div>
                  <div style={S.hdrCenter}>
                    <div style={S.hdrT1}>COMISSÃO LEILOEIRO</div>
                    <div style={S.hdrT2}>{d.leiloeiro_nome}</div>
                    <div style={S.hdrT3}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                    <div style={S.hdrT4}>{modoPeriodo ? `Período ${periodoLabel}` : periodoLabel}</div>
                  </div>
                  <div style={{ minWidth: 140, textAlign: 'right' }}>
                    <img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} />
                  </div>
                </div>

                {versao === 'basico' ? <VersaoBasica dados={d} /> : <VersaoDetalhada dados={d} />}

                <div style={S.footer}>www.rematei.com.br</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Comissão Leiloeiro — {periodoLabel}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, `Comissão Leiloeiro - ${periodoLabel}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Comissão Leiloeiro" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
